import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AdvancedBinNamingTemplateSchema,
  AdvancedBinNamingTemplateWithTypeSchema,
  BinNamingSettingsSchema,
} from "../bin-naming";
import {
  BinPrioritySchema,
  PersistentStorageLocationTypeSchema,
} from "./storage-location";

export const CoordinateSchema = z
  .object({
    x: z.number().meta({ privacy: privacy.nonPii }),
    y: z.number().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export type Coordinate = z.infer<typeof CoordinateSchema>;
export interface ICoordinate extends z.ZodType<Coordinate> {}

export enum CellType {
  RACK = "RACK",
  WALKWAY = "WALKWAY",
  STATION = "STATION",
}

export enum RackType {
  STANDARD = "STANDARD",
  OVERHEAD = "OVERHEAD",
}

export enum Direction {
  UP = "UP",
  DOWN = "DOWN",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

const WarehouseCellBase = z.object({
  x: z.number().meta({ privacy: privacy.nonPii }),
  y: z.number().meta({ privacy: privacy.nonPii }),
  type: createEnum(CellType, { enumName: "CellType" }).meta({
    privacy: privacy.nonPii,
  }),
});

/** Defines a range of bins with an associated storage location type */
export const StorageLocationTypeRangeSchema_ = z
  .object({
    /** Starting index of the range (0-based, inclusive) */
    start: z.number().int().min(0).meta({ privacy: privacy.nonPii }),
    /** Ending index of the range (0-based, inclusive) */
    end: z.number().int().min(0).meta({ privacy: privacy.nonPii }),
    /** Storage location type for bins in this range */
    type: PersistentStorageLocationTypeSchema.meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const StorageLocationTypeRangeSchema: z.ZodType<StorageLocationTypeRange> =
  StorageLocationTypeRangeSchema_;
export type StorageLocationTypeRange = z.infer<
  typeof StorageLocationTypeRangeSchema_
>;

/** A custom bin with a name and storage location type */
export const CustomBinSchema_ = z
  .object({
    isSellable: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
    name: z.string().meta({ privacy: privacy.nonPii }),
    type: PersistentStorageLocationTypeSchema.meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const CustomBinSchema: z.ZodType<CustomBin> = CustomBinSchema_;
export type CustomBin = z.infer<typeof CustomBinSchema_>;

export const RackCellSchema = WarehouseCellBase.extend({
  type: z.literal(CellType.RACK).meta({ privacy: privacy.nonPii }),
  bins: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .meta({ privacy: privacy.structured }),
  accessibleDirections: z
    .array(
      createEnum(Direction, { enumName: "Direction" }).meta({
        privacy: privacy.nonPii,
      }),
    )
    .meta({ privacy: privacy.structured }),
  name: z.string().nullish().meta({ privacy: privacy.nonPii }),
  shelfCount: z.number().nullish().meta({ privacy: privacy.nonPii }),
  rowName: z.string().nullish().meta({ privacy: privacy.nonPii }),
  namingSchema: BinNamingSettingsSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  advancedNamingTemplate: AdvancedBinNamingTemplateSchema.nullish().meta({
    privacy: privacy.structured,
  }),
  advancedNamingTemplates: z
    .array(
      AdvancedBinNamingTemplateWithTypeSchema.meta({
        privacy: privacy.structured,
      }),
    )
    .nullish()
    .meta({ privacy: privacy.structured }),
  rackType: createEnum(RackType, { enumName: "RackType" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  /** @deprecated Use typedCustomBins instead. One-off bin names without type info */
  customBins: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** One-off bin names with storage location type */
  typedCustomBins: z
    .array(CustomBinSchema.meta({ privacy: privacy.structured }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** Storage location type ranges - defines which bins have which type */
  storageLocationTypeRanges: z
    .array(StorageLocationTypeRangeSchema.meta({ privacy: privacy.structured }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  /** If set, all bins in this rack are this type (overrides storageLocationTypeRanges) */
  rackBinType: PersistentStorageLocationTypeSchema.nullish().meta({
    privacy: privacy.nonPii,
  }),
  /** If set, all bins in this rack have this pick priority */
  rackPriority: BinPrioritySchema.nullish().meta({ privacy: privacy.nonPii }),
  /** Whether generated bins in this rack can be picked from */
  isSellable: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
});
export type RackCell = z.infer<typeof RackCellSchema>;
export interface IRackCell extends z.ZodType<RackCell> {}

export const WalkwayCellSchema = WarehouseCellBase.extend({
  type: z.literal(CellType.WALKWAY).meta({ privacy: privacy.nonPii }),
});
export type WalkwayCell = z.infer<typeof WalkwayCellSchema>;

export const StationCellSchema = WarehouseCellBase.extend({
  type: z.literal(CellType.STATION).meta({ privacy: privacy.nonPii }),
  stationName: z.string().meta({ privacy: privacy.nonPii }),
  processingGroupId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  workstationId: z.string().nullish().meta({ privacy: privacy.nonPii }),
  unboxingVideoCaptureEnabled: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  offlineProcessingEnabled: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export type StationCell = z.infer<typeof StationCellSchema>;
export interface IStationCell extends z.ZodType<StationCell> {}

export const WarehouseCellSchema = z.discriminatedUnion("type", [
  RackCellSchema.meta({ privacy: privacy.structured }),
  WalkwayCellSchema.meta({ privacy: privacy.structured }),
  StationCellSchema.meta({ privacy: privacy.structured }),
]);

export type WarehouseCell = z.infer<typeof WarehouseCellSchema>;

export const WallSchema = z.object({
  from: CoordinateSchema.meta({ privacy: privacy.structured }),
  to: CoordinateSchema.meta({ privacy: privacy.structured }),
});
export type Wall = z.infer<typeof WallSchema>;
